import React, { forwardRef, useState, useEffect } from 'react';
import Table from '../../../component/atoms/Table';
import moment from 'moment';

import { useSelector } from 'react-redux';
import { AppState } from '../../../redux/modules/store';
import { DISPLAY_ITEM_ID } from '../../../redux/reducers/MasterInfoReducer';
import {
  getModelLongNameFromMasterInfo,
  getPcmCdDisplayOrderByPcmCdDivisionFromMasterInfo,
  getPcmCdNmByPcmCdDivisionFromMasterInfo,
  getPcmCdNmFromMasterInfo,
} from '../../../redux/reducers/MasterInfoReducer/utils/MasterInfoStateUtils';
import {
  arrayMobile,
  COMMODITY_CATEGORY_TYPE,
  COMMODITY_CATEGORY_TAG,
  MOBILE_INSTALL_EARLY_LAST_COUNT,
  MOBILE_INSTALL_MIDDLE_LAST_COUNT,
  countDiffMonth,
  getInquiryToday,
  arrayCarrierFree,
  stringDateToFormatString,
  stringDateToFormat,
  isInvalidDate,
  INITIAL_VALUE_TO_SELECTION,
} from '../../../Common';
import { setAmountComma, setStringToBrackets } from '../../../utils/StringUtil';
import { JapaneseDateStringType, getJapaneseDateTimeAsString } from '../../../utils/MomentUtil';

import PreviewContents from '../../atoms/PreviewContents';
import PreviewMiddleCategoryHeader from '../../atoms/PreviewMiddleCategoryHeader';
import PreviewSmallCategoryHeader from '../../atoms/PreviewSmallCategoryHeader';
import { ProductInfoDetailState, ProductVariableInfoPreviewState } from '../../../redux/reducers/CustomerInfoAllReducer';
import { isPastOrSameDate } from '../../../utils/ValidationCheck';
import { itemDetailGetApi } from '../../../external/ItemDetailApi';
import { pcmCdNmApi } from '../../../external/PcmCdNmApi';

import { areaCheckItemsList } from './definitions';

export interface ProductInfoDetailProps {
  item: ProductInfoDetailState;
  index: number;
  negotiationId: number;
  isEditable: boolean;
  openPermissionModal?: (type: string, negotiationId?: string, productId?: string, actionId?: string) => void;
}

interface ProductBaseProps {
  commodityCategoryType: string;
}

interface ProductHopeContentProps {
  showHopeKinds: boolean;
}

const getSetValueByDisplayItemId = (product: ProductInfoDetailState, displayItemId: string): string => {
  return product.productVariableInfo.find((variableInfo) => variableInfo.displayItemId === displayItemId)?.setValue || '';
};

export const getUpdateDatetimeByDisplayItemId = (product: ProductInfoDetailState, displayItemId: string): string => {
  return product.productVariableInfo.find((variableInfo) => variableInfo.displayItemId === displayItemId)?.updateDateTime || '';
};

const getSetValueListByDisplayItemId = (product: ProductInfoDetailState, displayItemId: string): string[] => {
  return product.productVariableInfo
    .filter((variableInfo) => variableInfo.displayItemId === displayItemId)
    .map((variableInfo: ProductVariableInfoPreviewState) => variableInfo.setValue || '');
};

// YYYYMM記述string型 Moment変換
const getYearMonth = (yearString: string, monthString: string): moment.Moment => {
  if (yearString === '' || monthString === '') {
    return moment(null);
  } else {
    return moment(moment(yearString.concat(monthString), 'YYYYMM').format('YYYYMM'), 'YYYYMM');
  }
};

// Moment型何月 分割残債計算
const getRemainingAmount = (fromYearMonth: moment.Moment, toYearMonth: moment.Moment, installmentBalance: string): number => {
  // Step1. 無効な日付は渡された場合、0を返却する
  const numInstallmentBalance = Number(installmentBalance);
  if (!fromYearMonth.isValid() || !toYearMonth.isValid() || isNaN(numInstallmentBalance)) {
    return 0;
  }
  // 日付の区間
  const betweenFromToMonths = toYearMonth.diff(fromYearMonth, 'M') + 1;
  if (betweenFromToMonths <= 0) {
    return 0;
  }
  return betweenFromToMonths * numInstallmentBalance;
};

const ProductInfoPreview = forwardRef<HTMLDivElement, ProductInfoDetailProps>(
  ({ item, index, negotiationId, isEditable, openPermissionModal }, lastDivItemRef) => {
    const masterInfo = useSelector((state: AppState) => state.masterInfo);
    const fetchLoadingStatus = useSelector((state: AppState) => state.loadingStatus.fetchCustomerInfoAll);

    // 「光 、SoftBank 光＋、Air 、でんわ」のCOMMODITY_CATEGORY_TYPE共通用
    const arrayInternet = [
      COMMODITY_CATEGORY_TYPE.SOFTBANK_HIKARI,
      COMMODITY_CATEGORY_TYPE.SOFTBANK_HIKARI_PLUS,
      COMMODITY_CATEGORY_TYPE.SOFTBANK_AIR,
      COMMODITY_CATEGORY_TYPE.HOME_PHONE,
    ];
    // ProductType別 PreviewContents uniqueId 命名処理
    const uniqueProductTypeWord =
      item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.SMARTPHONE
        ? 'product-smart-phone'
        : item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.HS_EXCEPT_SMARTPHONES
        ? 'product-hs-except-smart-phone'
        : item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.TABLET
        ? 'product-tablet'
        : item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.MOBILE_OTHER
        ? 'product-mobile-other'
        : item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.SOFTBANK_HIKARI
        ? 'product-sb-hikari'
        : item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.SOFTBANK_HIKARI_PLUS
        ? 'product-sb-hikari-plus'
        : item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.SOFTBANK_AIR
        ? 'product-sb-air'
        : item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.HOME_PHONE
        ? 'product-home-phone'
        : item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.ELECTRICITY
        ? 'product-electricity'
        : item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.SIM_ONLY
        ? 'product-sim-only'
        : item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.DATA_BOX
        ? 'product-data-box'
        : item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.BASKET_LIVE
        ? 'product-basket-live'
        : item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.SMARTPHONE_SET_SUPPORT
        ? 'product-smartphone-set-support'
        : item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.ALL_OTHER
        ? 'product-all-other'
        : '';

    /**
     * 商品 - HS（スマホ）
     * 商品 - HS（スマホ以外）
     * 商品 - タブレット
     * 商品 - その他 モバイル
     */

    const getDisplayValue = (displayItemId: DISPLAY_ITEM_ID): string => {
      return (item.productVariableInfo.length > 0 && getSetValueByDisplayItemId(item, displayItemId)) || '';
    };

    const getDisplayValueByVariableInfo = (
      displayItemId: DISPLAY_ITEM_ID,
      noteDisplayItemId?: DISPLAY_ITEM_ID,
      noteCode = '99',
    ): string => {
      const pcmCode = getDisplayValue(displayItemId);
      const displayValue: string = getPcmCdNmFromMasterInfo(masterInfo, displayItemId, pcmCode);
      const noteValue = noteDisplayItemId ? setStringToBrackets(getSetValueByDisplayItemId(item, noteDisplayItemId)) : '';
      return pcmCode === '' ? '' : pcmCode === '00' ? '' : pcmCode === noteCode ? displayValue + noteValue : displayValue;
    };

    // 業務日付取得
    const jobDateTime = fetchLoadingStatus.result?.jobDateTime;
    const jobDateMoment = getYearMonth(jobDateTime?.substring(0, 4) || '', jobDateTime?.substring(4, 6) || '');
    const jobDateFormat = getInquiryToday(jobDateTime);

    // 業務日付以降かの判定
    const isAfterJobDate = (date: moment.Moment) => {
      return date.isSameOrAfter(jobDateMoment);
    };

    // ブランド
    const displayCommodityBrand = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.COMMODITY_CATEGORY_BRAND);

    // 商談ステータス
    const displayNegotiationProgressType = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.NEGOTIATION_PROGRESS_TYPE);

    // 契約確率
    const displayContractAccuracyType =
      item.productStatusInfo.contractAccuracyType !== '00'
        ? getPcmCdNmByPcmCdDivisionFromMasterInfo(masterInfo, '0074', item.productStatusInfo.contractAccuracyType)
        : '';

    // 受付内容
    const displayReceptionContent =
      item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.SMARTPHONE
        ? getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.CONTRACT_TYPE_MOBILE)
        : item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.HS_EXCEPT_SMARTPHONES
        ? getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.CONTRACT_TYPE_MOBILE)
        : item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.TABLET
        ? getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.CONTRACT_TYPE_MOBILE)
        : item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.MOBILE_OTHER
        ? getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.CONTRACT_TYPE_MOBILE)
        : item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.SOFTBANK_HIKARI
        ? getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.CONTRACT_TYPE_HIKARI)
        : item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.SOFTBANK_HIKARI_PLUS
        ? getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.CONTRACT_TYPE_SB_HIKARI_PLUS)
        : item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.SOFTBANK_AIR
        ? getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.CONTRACT_TYPE_AIR)
        : item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.HOME_PHONE
        ? getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.CONTRACT_TYPE_HOME)
        : item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.ELECTRICITY
        ? getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.CONTRACT_TYPE_PWR)
        : item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.SIM_ONLY
        ? getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.CONTRACT_TYPE_MOBILE)
        : item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.DATA_BOX
        ? getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.CONTRACT_TYPE_DATA_BOX)
        : item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.BASKET_LIVE
        ? getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.CONTRACT_TYPE_BASKET_LIVE)
        : item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.SMARTPHONE_SET_SUPPORT
        ? getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.CONTRACT_TYPE_SMARTPHONE_SET_SUPPORT)
        : '';

    // 契約予定名義
    const displayContractorType = !item.contractorType
      ? ''
      : item.contractorType !== '00'
      ? getPcmCdNmByPcmCdDivisionFromMasterInfo(masterInfo, '0052', item.contractorType)
      : '';

    // 使用者予定
    const displayUserPlan = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.USER_PLAN);

    // 見込理由
    const displayRegisterReasonType = () => {
      const pcmCodeList = (
        item.productVariableInfo.length > 0 ? getSetValueListByDisplayItemId(item, DISPLAY_ITEM_ID.REGISTER_REASON_TYPE) : []
      ).sort((a, b) => (a < b ? -1 : 1));
      const registerReasonOther =
        item.productVariableInfo.length > 0
          ? setStringToBrackets(getSetValueByDisplayItemId(item, DISPLAY_ITEM_ID.REGISTER_REASON_TYPE_CONTENTS))
          : '';
      const displayRegisterReasonTypeList: string[] = [];
      pcmCodeList.forEach((pcmCode) => {
        displayRegisterReasonTypeList.push(
          pcmCode === '99'
            ? getPcmCdNmFromMasterInfo(masterInfo, DISPLAY_ITEM_ID.REGISTER_REASON_TYPE, pcmCode) + registerReasonOther
            : getPcmCdNmFromMasterInfo(masterInfo, DISPLAY_ITEM_ID.REGISTER_REASON_TYPE, pcmCode),
        );
      });
      return displayRegisterReasonTypeList.join('\n');
    };

    // 業務
    const displayContractTypeOther = getDisplayValueByVariableInfo(
      DISPLAY_ITEM_ID.CONTRACT_TYPE_OTHER,
      DISPLAY_ITEM_ID.OTHER_PRODUCT_BUSINESS_NOTE,
    );
    // 対応ステータス
    const displayOtherActionStatusType = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.OTHER_PRODUCT_ACTION_STATUS_TYPE);

    // 取り置き
    const displayProductKeepType = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.KEEP_TYPE, DISPLAY_ITEM_ID.KEEP_NOTE, '01');

    // 利用中キャリア
    const displayUsingMobileCarrier = getDisplayValueByVariableInfo(
      DISPLAY_ITEM_ID.USING_MOBILE_CARRIER,
      DISPLAY_ITEM_ID.USING_MOBILE_CARRIER_TEXT,
    );

    // 利用中機種
    const displayUsingMobileKind = () => {
      const usingMobileArray = [];
      // 利用中機種-機種タイプ
      const usingMobileKindType = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.USING_MOBILE_KIND_TYPE);
      // 利用中機種-機種名
      const usingMobileKindName = getDisplayValue(DISPLAY_ITEM_ID.USING_MOBILE_KIND_NAME);
      usingMobileKindType && usingMobileArray.push(usingMobileKindType);
      usingMobileKindName && usingMobileArray.push(usingMobileKindName);
      return usingMobileArray.join('／');
    };

    // 請求群
    const displayUsingMobileBillingGroupType = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.USING_MOBILE_BILLING_GROUP_TYPE);

    // 毎月のお支払い金額
    const usingMobileMonthlyPayment = getDisplayValue(DISPLAY_ITEM_ID.USING_MOBILE_MONTHLY_PAYMENT);
    const displayUsingMobileMonthlyPayment = usingMobileMonthlyPayment && setAmountComma(usingMobileMonthlyPayment) + '円';

    // 現在機種の利用開始年月 | 現在回線の利用開始年月 | 現在電力事業者の利用開始年月
    const getDisplayUsingStartYearMonth = (displayItemId: DISPLAY_ITEM_ID) => {
      let startYM = '';
      let countDiffM = '';
      const usingStartYM = getDisplayValue(displayItemId);
      if (usingStartYM) {
        startYM = getJapaneseDateTimeAsString(usingStartYM, JapaneseDateStringType.YEAR_MONTH);

        const formattedDateString = `${usingStartYM.substring(0, 4)}-${usingStartYM.substring(4, 6)}-01`;
        const startDateFormat = new Date(formattedDateString);
        const isValidYM = isPastOrSameDate(formattedDateString, jobDateTime?.substring(0, 8));
        countDiffM = (isValidYM && countDiffMonth(startDateFormat, jobDateFormat)) || '-';
      }
      return startYM && `利用開始年月：${startYM}\n`.concat(`開始年月からの月数：${setAmountComma(countDiffM)} ヵ月目`);
    };

    // 解約金
    const getDisplayCancellationFee = (displayItemId: DISPLAY_ITEM_ID) => {
      const cancellationFee = getDisplayValue(displayItemId);
      return cancellationFee && setAmountComma(cancellationFee) + '円';
    };

    // 下取り有無
    const displayUsingTradeInFlg = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.USING_TRADE_IN_FLG);

    // 毎月のデータ通信容量
    const usingDataTraffic = getDisplayValue(DISPLAY_ITEM_ID.USING_DATA_TRAFFIC);
    const displayUsingDataTraffic = usingDataTraffic && setAmountComma(usingDataTraffic) + 'GB以下';

    // 毎月の電子決済金額
    const electronicPayment = getDisplayValue(DISPLAY_ITEM_ID.ELECTRONIC_PAYMENT);
    const displayElectronicPayment = electronicPayment && setAmountComma(electronicPayment) + '円';

    // クレジットカードご利用状況
    const displayUsingMobileCreditCard = () => {
      const pcmCodeList = (
        item.productVariableInfo.length > 0 ? getSetValueListByDisplayItemId(item, DISPLAY_ITEM_ID.USING_MOBILE_CREDIT_CARD) : []
      ).sort((a, b) => (a < b ? -1 : 1));
      const displayMobileCreditCardList: string[] = [];
      pcmCodeList.forEach((pcmCode) => {
        displayMobileCreditCardList.push(getPcmCdNmFromMasterInfo(masterInfo, DISPLAY_ITEM_ID.USING_MOBILE_CREDIT_CARD, pcmCode));
      });
      return displayMobileCreditCardList.join('\n');
    };

    // モバイル更新月
    const displayMobileUpdYearMonth = getJapaneseDateTimeAsString(
      getDisplayValue(DISPLAY_ITEM_ID.MOBILE_UPD_YEAR_MONTH),
      JapaneseDateStringType.YEAR_MONTH,
    );

    // キャンペーン名
    const displayCampaignName = getDisplayValue(DISPLAY_ITEM_ID.SUBSCRIPTION_BENEFITS);

    // キャンペーン適用開始月
    const displayCampaignMonth = getJapaneseDateTimeAsString(
      getDisplayValue(DISPLAY_ITEM_ID.CAMPAIGN_START_MONTH),
      JapaneseDateStringType.YEAR_MONTH_DATE,
    );

    // 割賦残債（モバイル）
    // 前半終了回数
    const usingMobileInstallEarlyLastCount = () => {
      const setValue = getDisplayValue(DISPLAY_ITEM_ID.USING_MOBILE_INSTALL_EARLY_LAST_COUNT);
      return setValue === MOBILE_INSTALL_EARLY_LAST_COUNT.TWENTY_FOUR
        ? setValue
        : setValue === MOBILE_INSTALL_EARLY_LAST_COUNT.TWELVE
        ? setValue
        : '';
    };

    // 中間終了回数
    const usingMobileInstallMiddleLastCount = () => {
      const setValue = getDisplayValue(DISPLAY_ITEM_ID.USING_MOBILE_INSTALL_MIDDLE_LAST_COUNT);
      return setValue === MOBILE_INSTALL_MIDDLE_LAST_COUNT ? setValue : '';
    };

    // 前半割賦 ラベル名
    const displayMobileInstallEarlyLabel =
      usingMobileInstallEarlyLastCount() === MOBILE_INSTALL_EARLY_LAST_COUNT.TWELVE ? '1~12回目（12回）' : '1~24回目（24回）';

    // 中間割賦 ラベル名
    const displayMobileInstallMiddleLabel = '13~24回目（12回）';

    // 後半割賦 ラベル名
    const displayMobileInstallLatestLabel =
      usingMobileInstallEarlyLastCount() === MOBILE_INSTALL_EARLY_LAST_COUNT.TWENTY_FOUR
        ? '25~48回目（24回）'
        : usingMobileInstallMiddleLastCount() === MOBILE_INSTALL_MIDDLE_LAST_COUNT
        ? '25~48回目（24回）'
        : '13~48回目（36回）';

    // 割賦単価（後半）
    const mobileInstallMonthlyPaymentLatest = getDisplayValue(DISPLAY_ITEM_ID.MOBILE_INSTALL_MONTHLY_PAYMENT);
    const parseMobileInstallMonthlyPaymentLatest = String(parseInt(mobileInstallMonthlyPaymentLatest));
    const displayMobileInstallMonthlyPaymentLatest =
      parseMobileInstallMonthlyPaymentLatest !== 'NaN' ? setAmountComma(parseMobileInstallMonthlyPaymentLatest) + '円/月' : '';

    // 支払終了月（後半）
    const mobileInstallEndYearMonthLatest = getDisplayValue(DISPLAY_ITEM_ID.MOBILE_INSTALL_END_YEAR_MONTH);
    const displayMobileInstallEndYearMonthLatest = Number(mobileInstallEndYearMonthLatest)
      ? getJapaneseDateTimeAsString(mobileInstallEndYearMonthLatest, JapaneseDateStringType.YEAR_MONTH)
      : '';

    // 割賦単価（前半）
    const mobileInstallMonthlyPaymentEarly = getDisplayValue(DISPLAY_ITEM_ID.USING_MOBILE_INSTALL_MONTHLY_PAYMENT_EARLY);

    // 割賦単価（中間）
    const mobileInstallMonthlyPaymentMiddle = getDisplayValue(DISPLAY_ITEM_ID.USING_MOBILE_INSTALL_MONTHLY_PAYMENT_MIDDLE);

    // 支払終了月（前半）
    const mobileInstallEndYearMonthEarly = () => {
      const endYear = mobileInstallEndYearMonthLatest.substring(0, 4);
      const endMonth = mobileInstallEndYearMonthLatest.substring(4, 6);
      const prevYear =
        usingMobileInstallEarlyLastCount() === MOBILE_INSTALL_EARLY_LAST_COUNT.TWENTY_FOUR
          ? '2'
          : usingMobileInstallEarlyLastCount() === MOBILE_INSTALL_EARLY_LAST_COUNT.TWELVE
          ? '3 '
          : '';
      return Number(mobileInstallEndYearMonthLatest) ? prevYear && String(Number(endYear) - Number(prevYear)) + endMonth : '';
    };

    // 支払終了月（中間）
    const mobileInstallEndYearMonthMiddle = () => {
      const endYear = mobileInstallEndYearMonthLatest.substring(0, 4);
      const endMonth = mobileInstallEndYearMonthLatest.substring(4, 6);
      const prevYear = '2';
      return Number(mobileInstallEndYearMonthLatest) ? prevYear && String(Number(endYear) - Number(prevYear)) + endMonth : '';
    };

    //  支払終了月（前半）Moment
    const installmentEndYearMonthEarlyMoment = getYearMonth(
      mobileInstallEndYearMonthEarly().substring(0, 4),
      mobileInstallEndYearMonthEarly().substring(4, 6),
    );
    //  残債金額（前半）
    const installmentBalanceEarly = getRemainingAmount(jobDateMoment, installmentEndYearMonthEarlyMoment, mobileInstallMonthlyPaymentEarly);

    //  支払終了月（中間）Moment
    const installmentEndYearMonthMiddleMoment = getYearMonth(
      mobileInstallEndYearMonthMiddle().substring(0, 4),
      mobileInstallEndYearMonthMiddle().substring(4, 6),
    );
    //  残債金額（中間）
    const installmentBalanceMiddle = isAfterJobDate(installmentEndYearMonthEarlyMoment)
      ? // 前半終了月がシステム日付の年月以降である場合
        getRemainingAmount(
          installmentEndYearMonthEarlyMoment.clone().add(1, 'M'),
          installmentEndYearMonthMiddleMoment,
          mobileInstallMonthlyPaymentMiddle,
        )
      : // 前半終了月がシステム日付の年月よりも過去である場合
        getRemainingAmount(jobDateMoment, installmentEndYearMonthMiddleMoment, mobileInstallMonthlyPaymentMiddle);

    // 支払終了月（後半）Moment
    const installmentEndYearMonthLatestMoment = getYearMonth(
      mobileInstallEndYearMonthLatest.substring(0, 4),
      mobileInstallEndYearMonthLatest.substring(4, 6),
    );

    // 残債金額（後半）
    const installmentBalanceLatest = usingMobileInstallMiddleLastCount() // 中間残債金額が存在するかどうかで分岐
      ? isAfterJobDate(installmentEndYearMonthMiddleMoment)
        ? // 中間終了月がシステム日付の年月以降である場合
          getRemainingAmount(
            installmentEndYearMonthMiddleMoment.clone().add(1, 'M'), // getRemainingAmountは差分 + 1 の月数を元に計算する(同月考慮)ため、中間終了月に1ヶ月加算する
            installmentEndYearMonthLatestMoment,
            mobileInstallMonthlyPaymentLatest,
          )
        : // 中間終了月がシステム日付の年月よりも過去である場合
          getRemainingAmount(jobDateMoment, installmentEndYearMonthLatestMoment, mobileInstallMonthlyPaymentLatest)
      : isAfterJobDate(installmentEndYearMonthEarlyMoment)
      ? // 前半終了月がシステム日付の年月以降である場合
        getRemainingAmount(
          installmentEndYearMonthEarlyMoment.clone().add(1, 'M'), // getRemainingAmountは差分 + 1 の月数を元に計算する(同月考慮)ため、前半終了月に1ヶ月加算する
          installmentEndYearMonthLatestMoment,
          mobileInstallMonthlyPaymentLatest,
        )
      : // 前半終了月がシステム日付の年月よりも過去である場合
        getRemainingAmount(jobDateMoment, installmentEndYearMonthLatestMoment, mobileInstallMonthlyPaymentLatest);

    // 割賦表示文字列（後半）
    const displayMobileInstallmentBalanceLatest = () => {
      const mobileInstallmentBalanceLatestList = [];
      // 割賦単価
      const parseUsingMobileInstallMonthlyPaymentLatest = String(parseInt(mobileInstallMonthlyPaymentLatest));
      Number(mobileInstallMonthlyPaymentLatest) &&
        mobileInstallmentBalanceLatestList.push('割賦単価　：' + setAmountComma(parseUsingMobileInstallMonthlyPaymentLatest) + '円/月');

      // 支払終了月
      const displayMobileInstallEndYearMonthLatest =
        Number(mobileInstallEndYearMonthLatest) &&
        getJapaneseDateTimeAsString(mobileInstallEndYearMonthLatest, JapaneseDateStringType.YEAR_MONTH);
      displayMobileInstallEndYearMonthLatest &&
        mobileInstallmentBalanceLatestList.push('支払終了月：' + displayMobileInstallEndYearMonthLatest);

      // 残債金額
      displayMobileInstallEndYearMonthLatest &&
        mobileInstallmentBalanceLatestList.push('残債金額　：' + setAmountComma(String(installmentBalanceLatest)) + '円');

      return mobileInstallmentBalanceLatestList.join('\n');
    };

    // 割賦表示文字列（前半）
    const displayMobileInstallmentBalanceEarly = () => {
      const mobileInstallmentBalanceEarlyList = [];
      // 割賦単価
      const parseUsingMobileInstallMonthlyPaymentEarly = String(parseInt(mobileInstallMonthlyPaymentEarly));
      Number(mobileInstallMonthlyPaymentEarly) &&
        mobileInstallmentBalanceEarlyList.push('割賦単価　：' + setAmountComma(parseUsingMobileInstallMonthlyPaymentEarly) + '円/月');

      // 支払終了月
      const displayMobileInstallEndYearMonthEarly =
        Number(mobileInstallEndYearMonthEarly()) &&
        getJapaneseDateTimeAsString(mobileInstallEndYearMonthEarly(), JapaneseDateStringType.YEAR_MONTH);
      displayMobileInstallEndYearMonthEarly &&
        mobileInstallmentBalanceEarlyList.push('支払終了月：' + displayMobileInstallEndYearMonthEarly);
      // 残債金額
      displayMobileInstallEndYearMonthEarly &&
        mobileInstallmentBalanceEarlyList.push('残債金額　：' + setAmountComma(String(installmentBalanceEarly)) + '円');

      return mobileInstallmentBalanceEarlyList.join('\n');
    };

    // 割賦表示文字列（中間）
    const displayMobileInstallmentBalanceMiddle = () => {
      const mobileInstallmentBalanceMiddleList = [];
      // 割賦単価
      const parseUsingMobileInstallMonthlyPaymentMiddle = String(parseInt(mobileInstallMonthlyPaymentMiddle));
      Number(mobileInstallMonthlyPaymentMiddle) &&
        mobileInstallmentBalanceMiddleList.push('割賦単価　：' + setAmountComma(parseUsingMobileInstallMonthlyPaymentMiddle) + '円/月');

      // 支払終了月
      const displayMobileInstallEndYearMonthMiddle =
        Number(mobileInstallEndYearMonthMiddle()) &&
        getJapaneseDateTimeAsString(mobileInstallEndYearMonthMiddle(), JapaneseDateStringType.YEAR_MONTH);
      displayMobileInstallEndYearMonthMiddle &&
        mobileInstallmentBalanceMiddleList.push('支払終了月：' + displayMobileInstallEndYearMonthMiddle);
      // 残債金額
      displayMobileInstallEndYearMonthMiddle &&
        mobileInstallmentBalanceMiddleList.push('残債金額　：' + setAmountComma(String(installmentBalanceMiddle)) + '円');

      return mobileInstallmentBalanceMiddleList.join('\n');
    };

    // 残債総額 ：「支払回数ごとに設定」
    const installmentBalanceTotal = installmentBalanceEarly + installmentBalanceMiddle + installmentBalanceLatest;
    const displayInstallmentBalanceTotal =
      mobileInstallEndYearMonthLatest && mobileInstallEndYearMonthEarly()
        ? `残り ${setAmountComma(String(installmentBalanceTotal))}円`
        : undefined;

    // 残債総額 : 「簡易表示」
    const installmentBalanceSum = getRemainingAmount(jobDateMoment, installmentEndYearMonthLatestMoment, mobileInstallMonthlyPaymentLatest);
    const displayInstallmentBalanceSum = mobileInstallEndYearMonthLatest && `残り ${setAmountComma(String(installmentBalanceSum))}円`;

    // 割賦残債（SBAir）
    // 前半終了回数
    const usingSBAirInstallEarlyLastCount = () => {
      const EarlyLastCount = getDisplayValue(DISPLAY_ITEM_ID.AIR_FORMER_LAST_CNT);
      return !(Number(EarlyLastCount) < 1 || Number(EarlyLastCount) > 47) && Number.isInteger(Number(EarlyLastCount)) ? EarlyLastCount : '';
    };

    // 前半割賦 ラベル名
    const displayUsingSBAirInstallEarlyLabel =
      usingSBAirInstallEarlyLastCount() === '1'
        ? '1回目(1回)'
        : `1~${usingSBAirInstallEarlyLastCount()}回目(${usingSBAirInstallEarlyLastCount()}回)`;

    // 割賦単価（後半）
    const usingSBAirInstallMonthlyPaymentLatest = getDisplayValue(DISPLAY_ITEM_ID.AIR_INSTALL_MONTHLY_PAYMENT);
    const parseUsingSBAirInstallMonthlyPaymentLatest = String(parseInt(usingSBAirInstallMonthlyPaymentLatest));
    const displayUsingSBAirInstallMonthlyPaymentLatest =
      parseUsingSBAirInstallMonthlyPaymentLatest !== 'NaN' ? setAmountComma(parseUsingSBAirInstallMonthlyPaymentLatest) + '円/月' : '';

    // 支払終了月（後半）
    const usingSBAirInstallEndYearMonthLatest = isInvalidDate(
      stringDateToFormat(getDisplayValue(DISPLAY_ITEM_ID.AIR_INSTALL_END_YEAR_MONTH) + '01', 'yyyyMMdd'),
    )
      ? ''
      : getDisplayValue(DISPLAY_ITEM_ID.AIR_INSTALL_END_YEAR_MONTH);
    const displayUsingSBAirInstallEndYearMonthLatest = Number(usingSBAirInstallEndYearMonthLatest)
      ? getJapaneseDateTimeAsString(usingSBAirInstallEndYearMonthLatest, JapaneseDateStringType.YEAR_MONTH)
      : '';

    // 割賦単価（前半）
    const usingSBAirInstallMonthlyPaymentEarly = getDisplayValue(DISPLAY_ITEM_ID.AIR_INSTALL_MONTHLY_PAYMENT_EARLY);

    // 支払終了月（前半）
    const usingSBAirInstallEndYearMonthEarly = getDisplayValue(DISPLAY_ITEM_ID.AIR_INSTALL_END_YEAR_MONTH_EARLY);

    //  支払終了月（前半）Moment
    const usingSBAirInstallmentEndYearMonthEarlyMoment = getYearMonth(
      usingSBAirInstallEndYearMonthEarly.substring(0, 4),
      usingSBAirInstallEndYearMonthEarly.substring(4, 6),
    );

    //  残債金額（前半）
    const usingSBAirInstallmentBalanceEarly = getRemainingAmount(
      jobDateMoment,
      usingSBAirInstallmentEndYearMonthEarlyMoment,
      usingSBAirInstallMonthlyPaymentEarly,
    );

    // 支払終了月（後半）Moment
    const usingSBAirInstallmentEndYearMonthLatestMoment = getYearMonth(
      usingSBAirInstallEndYearMonthLatest.substring(0, 4),
      usingSBAirInstallEndYearMonthLatest.substring(4, 6),
    );

    // 後半支払回数
    const usingSBAirBetweenFromToMonths = usingSBAirInstallmentEndYearMonthLatestMoment.diff(
      usingSBAirInstallmentEndYearMonthEarlyMoment,
      'M',
    );
    // 後半割賦 ラベル名
    const displayUsingSBAirInstallLatestLabel =
      usingSBAirBetweenFromToMonths !== 1
        ? `${String(Number(usingSBAirInstallEarlyLastCount()) + 1)}~48回目(${48 - Number(usingSBAirInstallEarlyLastCount())}回)`
        : '48回目(1回)';

    // 残債金額（後半）
    const usingSBAirInstallmentBalanceLatest = isAfterJobDate(usingSBAirInstallmentEndYearMonthEarlyMoment)
      ? // 前半終了月がシステム日付の年月以降である場合
        getRemainingAmount(
          usingSBAirInstallmentEndYearMonthEarlyMoment.clone().add(1, 'M'), // getRemainingAmountは差分 + 1 の月数を元に計算する(同月考慮)ため、前半終了月に1ヶ月加算する
          usingSBAirInstallmentEndYearMonthLatestMoment,
          usingSBAirInstallMonthlyPaymentLatest,
        )
      : // 前半終了月がシステム日付の年月よりも過去である場合
        getRemainingAmount(jobDateMoment, usingSBAirInstallmentEndYearMonthLatestMoment, usingSBAirInstallMonthlyPaymentLatest);

    // 割賦表示文字列（後半）
    const displayUsingSBAirInstallmentBalanceLatest = () => {
      const usingSBAirInstallmentBalanceLatestList = [];
      // 割賦単価
      const parseUsingSBAirInstallMonthlyPaymentLatest = String(parseInt(usingSBAirInstallMonthlyPaymentLatest));
      Number(usingSBAirInstallMonthlyPaymentLatest) &&
        usingSBAirInstallmentBalanceLatestList.push('割賦単価　：' + setAmountComma(parseUsingSBAirInstallMonthlyPaymentLatest) + '円/月');

      // 支払終了月
      const displayUsingSBAirInstallEndYearMonthLatest =
        Number(usingSBAirInstallEndYearMonthLatest) &&
        getJapaneseDateTimeAsString(usingSBAirInstallEndYearMonthLatest, JapaneseDateStringType.YEAR_MONTH);
      displayUsingSBAirInstallEndYearMonthLatest &&
        usingSBAirInstallmentBalanceLatestList.push('支払終了月：' + displayUsingSBAirInstallEndYearMonthLatest);

      // 残債金額
      displayUsingSBAirInstallEndYearMonthLatest &&
        usingSBAirInstallmentBalanceLatestList.push('残債金額　：' + setAmountComma(String(usingSBAirInstallmentBalanceLatest)) + '円');

      return usingSBAirInstallmentBalanceLatestList.join('\n');
    };

    // 割賦表示文字列（前半）
    const displayUsingSBAirInstallmentBalanceEarly = () => {
      const usingSBAirInstallmentBalanceEarlyList = [];
      // 割賦単価
      const parseUsingSBAirInstallMonthlyPaymentEarly = String(parseInt(usingSBAirInstallMonthlyPaymentEarly));
      Number(usingSBAirInstallMonthlyPaymentEarly) &&
        usingSBAirInstallmentBalanceEarlyList.push('割賦単価　：' + setAmountComma(parseUsingSBAirInstallMonthlyPaymentEarly) + '円/月');

      // 支払終了月
      const displayUsingSBAirInstallEndYearMonthEarly =
        Number(usingSBAirInstallEndYearMonthEarly) &&
        getJapaneseDateTimeAsString(usingSBAirInstallEndYearMonthEarly, JapaneseDateStringType.YEAR_MONTH);
      displayUsingSBAirInstallEndYearMonthEarly &&
        usingSBAirInstallmentBalanceEarlyList.push('支払終了月：' + displayUsingSBAirInstallEndYearMonthEarly);
      // 残債金額
      displayUsingSBAirInstallEndYearMonthEarly &&
        usingSBAirInstallmentBalanceEarlyList.push('残債金額　：' + setAmountComma(String(usingSBAirInstallmentBalanceEarly)) + '円');

      return usingSBAirInstallmentBalanceEarlyList.join('\n');
    };

    // 残債総額 ：「支払回数ごとに設定」
    const usingSBAirInstallmentBalanceTotal = usingSBAirInstallmentBalanceEarly + usingSBAirInstallmentBalanceLatest;
    const displayUsingSBAirInstallmentBalanceTotal =
      usingSBAirInstallEndYearMonthLatest && usingSBAirInstallEndYearMonthEarly
        ? `残り ${setAmountComma(String(usingSBAirInstallmentBalanceTotal))}円`
        : undefined;

    // 残債総額 : 「簡易表示」
    const usingSBAirInstallmentBalanceSum = getRemainingAmount(
      jobDateMoment,
      usingSBAirInstallmentEndYearMonthLatestMoment,
      usingSBAirInstallMonthlyPaymentLatest,
    );
    const displayUsingSBAirInstallmentBalanceSum =
      usingSBAirInstallEndYearMonthLatest && `残り ${setAmountComma(String(usingSBAirInstallmentBalanceSum))}円`;

    // 割賦残債（おうちのでんわ）
    // 割賦単価
    const landlinePhoneInstallMonthlyPaymentLatest = getDisplayValue(DISPLAY_ITEM_ID.LANDLINE_PHONE_INSTALL_MONTHLY_PAYMENT);
    const parseLandlinePhoneInstallMonthlyPaymentLatest = String(parseInt(landlinePhoneInstallMonthlyPaymentLatest));
    const displayLandlinePhoneInstallMonthlyPaymentLatest =
      parseLandlinePhoneInstallMonthlyPaymentLatest !== 'NaN'
        ? setAmountComma(parseLandlinePhoneInstallMonthlyPaymentLatest) + '円/月'
        : '';

    // 支払終了月
    const landlinePhoneInstallEndYearMonthLatest = isInvalidDate(
      stringDateToFormat(getDisplayValue(DISPLAY_ITEM_ID.LANDLINE_PHONE_INSTALL_END_YEAR_MONTH) + '01', 'yyyyMMdd'),
    )
      ? ''
      : getDisplayValue(DISPLAY_ITEM_ID.LANDLINE_PHONE_INSTALL_END_YEAR_MONTH);
    const displayLandlinePhoneInstallEndYearMonthLatest = Number(landlinePhoneInstallEndYearMonthLatest)
      ? getJapaneseDateTimeAsString(landlinePhoneInstallEndYearMonthLatest, JapaneseDateStringType.YEAR_MONTH)
      : '';

    // 支払終了月Moment
    const landlinePhoneInstallmentEndYearMonthLatestMoment = getYearMonth(
      landlinePhoneInstallEndYearMonthLatest.substring(0, 4),
      landlinePhoneInstallEndYearMonthLatest.substring(4, 6),
    );

    // 残債総額 : 「簡易表示」
    const landlinePhoneInstallmentBalanceSum = getRemainingAmount(
      jobDateMoment,
      landlinePhoneInstallmentEndYearMonthLatestMoment,
      landlinePhoneInstallMonthlyPaymentLatest,
    );
    const displayLandlinePhoneInstallmentBalanceSum =
      landlinePhoneInstallEndYearMonthLatest && `残り ${setAmountComma(String(landlinePhoneInstallmentBalanceSum))}円`;

    // 希望機種有無
    const displayHopKindsFlg = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.HOPE_KINDS_FLG, DISPLAY_ITEM_ID.HOPE_KINDS_REMARKS, '02');
    // 希望機種-希望機種タイプ(／希望機種名)(希望機種名取得でPromiseを用いるためuseEffect-setStateで対応)
    const [hopeKindsInfo, setHopeKindsInfo] = useState<string | undefined>(undefined);

    useEffect(() => {
      const hopeKindsArray: string[] = [];
      // 希望機種-機種タイプ
      const hopeKindsType = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.HOPE_KINDS_TYPE);
      hopeKindsType && hopeKindsType !== '未選択' && hopeKindsArray.push(hopeKindsType);
      // 希望機種-機種名
      const hopeKindsName = getModelLongNameFromMasterInfo(masterInfo, getDisplayValue(DISPLAY_ITEM_ID.HOPE_KINDS_NAME));
      const hopeKindsNameValue = getSetValueByDisplayItemId(item, DISPLAY_ITEM_ID.HOPE_KINDS_NAME);
      if (!hopeKindsName && hopeKindsNameValue) {
        itemDetailGetApi({ itemCode: hopeKindsNameValue }).then((resolve) => {
          resolve.item.displayName && hopeKindsArray.push(resolve.item.displayName);
          setHopeKindsInfo(hopeKindsArray.join('／'));
        });
      } else {
        hopeKindsName && hopeKindsArray.push(hopeKindsName);
        setHopeKindsInfo(hopeKindsArray.join('／'));
      }
    }, [item]);

    // 料金プラン (sb, ym)
    const displayPricePlan =
      getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.PRICE_PLAN) || getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.YM_PRICE_PLAN);

    // パケットサービス
    const masterPacketDsctService = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.PACKET_DSCT_SERVICE);
    const [displayPacketDsctService, setDisplayPacketDsctService] = useState(masterPacketDsctService);

    useEffect(() => {
      const packetDsctServicePcmCode = getDisplayValue(DISPLAY_ITEM_ID.PACKET_DSCT_SERVICE);
      if (!masterPacketDsctService && packetDsctServicePcmCode && packetDsctServicePcmCode !== INITIAL_VALUE_TO_SELECTION) {
        pcmCdNmApi({
          displayItemId: DISPLAY_ITEM_ID.PACKET_DSCT_SERVICE,
          pcmCd: packetDsctServicePcmCode,
          targetDate: getUpdateDatetimeByDisplayItemId(item, DISPLAY_ITEM_ID.PACKET_DSCT_SERVICE),
        })
          .then((resolve) => {
            // PCMコード名称が取得できた場合のみ表示を更新
            if (resolve.pcmCdNm) {
              setDisplayPacketDsctService(resolve.pcmCdNm);
            }
          })
          .catch((error) => {
            console.error('PCM API Error in ProductInfoPreview:', error);
            // API失敗時は表示名なしで表示を続ける
          });
      }
    }, [item, masterPacketDsctService]);

    // 店頭スマホサポート
    const displaySmartPhoneSetSupport = () => {
      const pcmCodeList =
        item.productVariableInfo.length > 0 ? getSetValueListByDisplayItemId(item, DISPLAY_ITEM_ID.SMART_PHONE_SET_SUPPORT) : [];

      const pcmCdDisplayOrder = getPcmCdDisplayOrderByPcmCdDivisionFromMasterInfo(
        masterInfo,
        DISPLAY_ITEM_ID.SMART_PHONE_SET_SUPPORT,
        pcmCodeList,
      );

      const registerReasonOther =
        item.productVariableInfo.length > 0
          ? setStringToBrackets(getSetValueByDisplayItemId(item, DISPLAY_ITEM_ID.SMART_PHONE_SET_SUPPORT_TEXT))
          : '';
      const displayRegisterReasonTypeList: string[] = [];
      pcmCdDisplayOrder.forEach((pcmCode) => {
        displayRegisterReasonTypeList.push(
          pcmCode === '99'
            ? getPcmCdNmFromMasterInfo(masterInfo, DISPLAY_ITEM_ID.SMART_PHONE_SET_SUPPORT, pcmCode) + registerReasonOther
            : getPcmCdNmFromMasterInfo(masterInfo, DISPLAY_ITEM_ID.SMART_PHONE_SET_SUPPORT, pcmCode),
        );
      });
      return displayRegisterReasonTypeList.join('\n');
    };

    // PayPay設定
    const paypaySetting = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.PAYPAY_SETTING);

    // 特記事項
    const displayNotice = ({ commodityCategoryType }: ProductBaseProps) => {
      const pcmCodeList = (
        item.productVariableInfo.length > 0 ? getSetValueListByDisplayItemId(item, DISPLAY_ITEM_ID.PRODUCT_NOTICE_AGE_CHECK) : []
      ).sort((a, b) => (a < b ? -1 : 1));
      const displayNoticeList: string[] = [];
      pcmCodeList.forEach((pcmCode) => {
        if (
          !(
            [COMMODITY_CATEGORY_TYPE.TABLET, COMMODITY_CATEGORY_TYPE.MOBILE_OTHER].includes(
              commodityCategoryType as COMMODITY_CATEGORY_TYPE,
            ) && pcmCode === '03'
          )
        ) {
          displayNoticeList.push(getPcmCdNmFromMasterInfo(masterInfo, DISPLAY_ITEM_ID.PRODUCT_NOTICE_AGE_CHECK, pcmCode));
        }
      });
      return displayNoticeList.join('\n');
    };

    // エリアチェック結果
    const displayAreaCheckStatusByCategory = arrayInternet.includes(item.commodityCategoryType as COMMODITY_CATEGORY_TYPE)
      ? areaCheckItemsList[item.commodityCategoryType].map((subitem) => {
          const value =
            subitem.type === 'radio' ? getDisplayValueByVariableInfo(subitem.itemId) : getSetValueByDisplayItemId(item, subitem.itemId);

          return {
            ...subitem,
            value: subitem.type === 'limit' ? stringDateToFormatString(value || '', 'yyyyMMdd', 'yyyy/MM/dd') : value || '',
          };
        })
      : [];

    // 契約中回線事業者
    const displayUsingInternetLineProvider = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.USING_INTERNETLINE_PROVIDER);

    // 契約中回線事業者備考欄
    const displayUsingInternetLineProviderRemarks = getDisplayValue(DISPLAY_ITEM_ID.USING_INTERNETLINE_PROVIDER_REMARKS);

    const remarksPrefix = '備考欄 : ';
    // 契約中回線事業者
    const displayUsingInternetLineProviderInfo = [
      displayUsingInternetLineProvider,
      `${displayUsingInternetLineProviderRemarks && remarksPrefix}${displayUsingInternetLineProviderRemarks}`,
    ]
      .filter((v) => v) // ブランクは除外
      .join('\n');

    // 契約中プロバイダ
    const displayUsingLandLineProvider = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.USING_LANDLINE_PHONE_PROVIDER);

    // 契約中プロバイダ備考欄
    const displayUsingLandLineProviderRemarks = getDisplayValue(DISPLAY_ITEM_ID.USING_LANDLINE_PHONE_PROVIDER_REMARKS);

    // 契約中プロバイダ
    const displayUsingLandLineProviderInfo = [
      displayUsingLandLineProvider,
      `${displayUsingLandLineProviderRemarks && remarksPrefix}${displayUsingLandLineProviderRemarks}`,
    ]
      .filter((v) => v) // ブランクは除外
      .join('\n');

    // 契約中回線事業者（SoftBank 光＋）
    const displayUsingInternetLineProviderSBHikariPlus = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.USING_INTERNETLINE_PROVIDER);
    // 契約中回線事業者備考欄（SoftBank 光＋）
    const displayUsingInternetLineProviderSBHikariPlusRemarks = getDisplayValue(DISPLAY_ITEM_ID.USING_INTERNETLINE_PROVIDER_REMARKS);
    // 契約中回線事業者（SoftBank 光＋）
    const displayUsingInternetLineProviderInfoSBHikariPlus = [
      displayUsingInternetLineProviderSBHikariPlus,
      `${displayUsingInternetLineProviderSBHikariPlusRemarks && remarksPrefix}${displayUsingInternetLineProviderSBHikariPlusRemarks}`,
    ]
      .filter((v) => v) // ブランクは除外
      .join('\n');

    // 契約中プロバイダ（SoftBank 光＋）
    const displayUsingLandLineProviderSBHikariPlus = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.USING_LANDLINE_PHONE_PROVIDER);
    // 契約中プロバイダ備考欄（SoftBank 光＋）
    const displayUsingLandLineProviderSBHikariPlusRemarks = getDisplayValue(DISPLAY_ITEM_ID.USING_LANDLINE_PHONE_PROVIDER_REMARKS);
    // 契約中プロバイダ（SoftBank 光＋）
    const displayUsingLandLineProviderInfoSBHikariPlus = [
      displayUsingLandLineProviderSBHikariPlus,
      `${displayUsingLandLineProviderSBHikariPlusRemarks && remarksPrefix}${displayUsingLandLineProviderSBHikariPlusRemarks}`,
    ]
      .filter((v) => v) // ブランクは除外
      .join('\n');

    // お住まい
    const displayHousingType = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.HOUSING_TYPE);

    // 利用中のネット環境
    const displayUsingHomeInternetType = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.USING_HOME_INTERNET_TYPE);

    // 固定電話(おうちでんわ)
    const displayFixedLineStatus = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.LANDLINE_PHONE_USAGE_STATUS);

    // 固定電話(SB光)
    const displayFixedLineStatusSbHikari = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.FIXED_LINE_STATUS_SB_HIKARI);

    // 毎月のお支払い金額(SB光)
    const usingSbHikariMonthlyPayment = getDisplayValue(DISPLAY_ITEM_ID.USING_SB_HIKARI_MONTHLY_PAYMENT);
    const displayUsingSbHikariMonthlyPayment = usingSbHikariMonthlyPayment && setAmountComma(usingSbHikariMonthlyPayment) + '円';

    // 毎月のお支払い金額(SoftBank 光＋)
    const usingMonthlyPaymentSBHikariPlus = getDisplayValue(DISPLAY_ITEM_ID.USING_MONTHLY_PAYMENT_SB_HIKARI_PLUS);
    const displayUsingSBHikariPlusMonthlyPayment =
      usingMonthlyPaymentSBHikariPlus && setAmountComma(usingMonthlyPaymentSBHikariPlus) + '円';

    // 毎月のお支払い金額(SBAir)
    const usingSbAirMonthlyPayment = getDisplayValue(DISPLAY_ITEM_ID.USING_SB_AIR_MONTHLY_PAYMENT);
    const displayUsingSbAirMonthlyPayment = usingSbAirMonthlyPayment && setAmountComma(usingSbAirMonthlyPayment) + '円';

    // 毎月のお支払い金額(おうちでんわ)
    const usingLandlinePhoneMonthlyPayment = getDisplayValue(DISPLAY_ITEM_ID.USING_LANDLINE_PHONE_MONTHLY_PAYMENT);
    const displayUsingLandlinePhoneMonthlyPayment =
      usingLandlinePhoneMonthlyPayment && setAmountComma(usingLandlinePhoneMonthlyPayment) + '円';

    // NTT116確認結果
    const displayInternetContractHolder = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.INTERNETLINE_CONTRACT_HOLDER);

    // SB光更新月
    const displayInternetUpdYearMonth = getJapaneseDateTimeAsString(
      getDisplayValue(DISPLAY_ITEM_ID.USING_INTERNETLINE_UPD_YEAR_MONTH),
      JapaneseDateStringType.YEAR_MONTH,
    );

    // 現在の電力事業者
    const displayUsingPwrProvider = getDisplayValueByVariableInfo(
      DISPLAY_ITEM_ID.USING_PWR_PROVIDER,
      DISPLAY_ITEM_ID.USING_PWR_PROVIDER_NAME,
      '0',
    );

    // 電気料金
    const displayUsingPwrMonthCharge = () => {
      const displayUsingPwrMonthChargeList: string[] = [];
      // 月
      const usingPwrMonth = getDisplayValue(DISPLAY_ITEM_ID.USING_PWR_MONTH);
      // 料金金額
      const usingPwrMonthCharge = getDisplayValue(DISPLAY_ITEM_ID.USING_PWR_MONTH_CHARGE);
      usingPwrMonth !== '' && displayUsingPwrMonthChargeList.push(Number(usingPwrMonth) + '月の料金');
      usingPwrMonthCharge !== '' && displayUsingPwrMonthChargeList.push(setAmountComma(usingPwrMonthCharge) + '円');
      return displayUsingPwrMonthChargeList.join('\n');
    };

    // お住まい(おうちのでんき)
    const displayUsingPwrHousingType = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.USING_PWR_HOUSING_TYPE);

    // ご希望プラン
    const displayPwrHopePricePlan = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.PWR_HOPE_PRICE_PLAN);

    // 自社／他社ユーザー（回線）（あんしんデータボックス）
    const displayUsingUserCompanyDataBox = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.USER_COMPANY_DATA_BOX);

    // 自社／他社ユーザー（回線）（バスケットLIVE）
    const displayUsingUserCompanyBasketLive = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.USER_COMPANY_BASKET_LIVE);

    // 自社／他社ユーザー（回線）（店頭スマホサポート（他社））
    const displayUsingUserCompanySmartphoneSetSupport = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.USER_COMPANY_SMARTPHONE_SET_SUPPORT);

    // お支払い方法（あんしんデータボックス）
    const displayPaymentMethodDataBox = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.PAYMENT_METHOD_DATA_BOX);

    // お支払い方法（バスケットLIVE）
    const displayPaymentMethodBasketLive = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.PAYMENT_METHOD_BASKET_LIVE);

    // 料金プラン（バスケットLIVE）
    const displayPricePlanBasketLive = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.PRICE_PLAN_BASKET_LIVE);

    // お支払い方法（店頭スマホサポート（他社））
    const displayPaymentMethodSmartphoneSetSupport = getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.PAYMENT_METHOD_SMARTPHONE_SET_SUPPORT);

    // プラン（店頭スマホサポート（他社））
    const displayPlanSmartphoneSetSupport = () => {
      const pcmCodeList = (
        item.productVariableInfo.length > 0 ? getSetValueListByDisplayItemId(item, DISPLAY_ITEM_ID.PLAN_SMARTPHONE_SET_SUPPORT) : []
      ).sort((a, b) => (a < b ? -1 : 1));
      const planOther =
        item.productVariableInfo.length > 0
          ? setStringToBrackets(getSetValueByDisplayItemId(item, DISPLAY_ITEM_ID.PLAN_SMARTPHONE_SET_SUPPORT_TEXT))
          : '';
      const displayPlanSmartphoneSetSupportList: string[] = [];
      pcmCodeList.forEach((pcmCode) => {
        displayPlanSmartphoneSetSupportList.push(
          pcmCode === '99'
            ? getPcmCdNmFromMasterInfo(masterInfo, DISPLAY_ITEM_ID.PLAN_SMARTPHONE_SET_SUPPORT, pcmCode) + planOther
            : getPcmCdNmFromMasterInfo(masterInfo, DISPLAY_ITEM_ID.PLAN_SMARTPHONE_SET_SUPPORT, pcmCode),
        );
      });
      return displayPlanSmartphoneSetSupportList.join('\n');
    };

    // 取り置き jsx
    const KeepTypeInfo = () => (
      <PreviewContents
        title='取り置き'
        uniqueId={`${uniqueProductTypeWord}-keep-type-${item.productId}`}
        paragraphValue={displayProductKeepType}
      />
    );

    const CommodityBrandInfo = () => (
      <PreviewContents
        title='ブランド'
        uniqueId={`${uniqueProductTypeWord}-commodity-brand-${item.productId}`}
        paragraphValue={displayCommodityBrand}
      />
    );

    // 基本情報 Jsx
    const BasicInfo = ({ commodityCategoryType }: ProductBaseProps) => (
      <Table.TBody>
        {/* header (small) */}
        <PreviewSmallCategoryHeader title='基本情報' uniqueId={`${uniqueProductTypeWord}-base-info-heading-${item.productId}`} />
        {commodityCategoryType === COMMODITY_CATEGORY_TYPE.ALL_OTHER ? (
          <>
            {/* preview contents */}
            <PreviewContents
              title='業務'
              uniqueId={`${uniqueProductTypeWord}-contract-type-other-${item.productId}`}
              paragraphValue={displayContractTypeOther}
            />

            {/* preview contents */}
            <PreviewContents
              title='対応ステータス'
              uniqueId={`${uniqueProductTypeWord}-other-action-status-type-${item.productId}`}
              paragraphValue={displayOtherActionStatusType}
            />

            {/* preview contents */}
            <KeepTypeInfo />
          </>
        ) : (
          <>
            {arrayMobile.includes(commodityCategoryType as COMMODITY_CATEGORY_TYPE) && <CommodityBrandInfo />}
            <PreviewContents
              title='商談ステータス'
              uniqueId={`${uniqueProductTypeWord}-negotiation-progress-type-${item.productId}`}
              paragraphValue={displayNegotiationProgressType}
            />

            {/* preview contents */}
            <PreviewContents
              title='契約確率'
              uniqueId={`${uniqueProductTypeWord}-contract-accuracy-type-${item.productId}`}
              paragraphValue={displayContractAccuracyType}
            />

            {/* preview contents */}
            <PreviewContents
              title='受付内容'
              uniqueId={`${uniqueProductTypeWord}-reception-content-${item.productId}`}
              paragraphValue={displayReceptionContent}
            />

            {/* preview contents */}
            <PreviewContents
              title='契約予定名義'
              uniqueId={`${uniqueProductTypeWord}-contractor-type-${item.productId}`}
              paragraphValue={displayContractorType}
            />

            {/* preview contents */}
            {arrayMobile.includes(commodityCategoryType as COMMODITY_CATEGORY_TYPE) && (
              <PreviewContents
                title='使用者予定'
                uniqueId={`${uniqueProductTypeWord}-user-plan-${item.productId}`}
                paragraphValue={displayUserPlan}
              />
            )}

            {/* preview contents */}
            <PreviewContents
              title='見込理由'
              uniqueId={`${uniqueProductTypeWord}-register-reason-type-${item.productId}`}
              paragraphValue={displayRegisterReasonType()}
            />
          </>
        )}
      </Table.TBody>
    );

    //エリアチェック
    const AreaCheckInfo = () => {
      return (
        <>
          <Table.TBody>
            {/* header (small) */}
            <PreviewSmallCategoryHeader title='エリアチェック' uniqueId={`${uniqueProductTypeWord}-area-check-heading-${item.productId}`} />
            {/* preview contents */}
            {displayAreaCheckStatusByCategory.map((subitem, index) => (
              <PreviewContents
                title={`${subitem.title}${subitem.type === 'radio' ? ' エリアチェック結果' : ''}`}
                key={`${uniqueProductTypeWord}-information-area-check-status-${item.productId}-${index}`}
                uniqueId={`${uniqueProductTypeWord}-information-area-check-status-${item.productId}-${index}`}
                paragraphValue={subitem.value}
              />
            ))}
          </Table.TBody>
        </>
      );
    };
    // ご利用状況 Jsx
    const UsageStatusInfo = (commodityCategoryType: string) => {
      const uniqueKeyWord = arrayMobile.includes(commodityCategoryType as COMMODITY_CATEGORY_TYPE)
        ? 'mobile-line'
        : arrayInternet.includes(commodityCategoryType as COMMODITY_CATEGORY_TYPE)
        ? 'internet-line'
        : commodityCategoryType === COMMODITY_CATEGORY_TYPE.ELECTRICITY
        ? 'pwr-provider'
        : commodityCategoryType === COMMODITY_CATEGORY_TYPE.DATA_BOX
        ? 'data-box'
        : commodityCategoryType === COMMODITY_CATEGORY_TYPE.BASKET_LIVE
        ? 'basket-live'
        : 'smartphone-set-support';
      return (
        <Table.TBody>
          {/* header (small) */}
          <PreviewSmallCategoryHeader
            title='ご利用状況'
            uniqueId={`${uniqueProductTypeWord}-${uniqueKeyWord}-usage-status-heading-${item.productId}`}
          />
          {arrayMobile.includes(commodityCategoryType as COMMODITY_CATEGORY_TYPE) ? (
            <>
              {/* preview contents */}
              <PreviewContents
                title='利用中キャリア'
                uniqueId={`${uniqueProductTypeWord}-using-mobile-carrier-${item.productId}`}
                paragraphValue={displayUsingMobileCarrier}
              />
              {/* preview contents */}
              <PreviewContents
                title='利用中機種'
                uniqueId={`${uniqueProductTypeWord}-using-mobile-kind-${item.productId}`}
                paragraphValue={displayUsingMobileKind()}
              />
              {/* preview contents */}
              <PreviewContents
                title='請求群'
                uniqueId={`${uniqueProductTypeWord}-using-mobile-billing-group-type-${item.productId}`}
                paragraphValue={displayUsingMobileBillingGroupType}
              />
              {/* preview contents */}
              <PreviewContents
                title='毎月のお支払い金額'
                uniqueId={`${uniqueProductTypeWord}-using-mobile-monthly-payment-${item.productId}`}
                paragraphValue={displayUsingMobileMonthlyPayment}
              />
              {/* preview contents */}
              <PreviewContents
                title='現在機種の利用開始年月'
                uniqueId={`${uniqueProductTypeWord}-using-start-year-month-${item.productId}`}
                paragraphValue={getDisplayUsingStartYearMonth(DISPLAY_ITEM_ID.USING_MOBILE_START_YEAR_MONTH)}
              />
              {/* preview contents */}
              <PreviewContents
                title='解約金'
                uniqueId={`${uniqueProductTypeWord}-cancellation-fee-${item.productId}`}
                paragraphValue={getDisplayCancellationFee(DISPLAY_ITEM_ID.MOBILE_CANCELLATION_FEE)}
              />
              {/* preview contents */}
              <PreviewContents
                title='下取り有無'
                uniqueId={`${uniqueProductTypeWord}-using-trade-in-flg-${item.productId}`}
                paragraphValue={displayUsingTradeInFlg}
              />
              {/* preview contents */}
              <PreviewContents
                title='毎月のデータ通信容量'
                uniqueId={`${uniqueProductTypeWord}-using-data-traffic-${item.productId}`}
                paragraphValue={displayUsingDataTraffic}
              />
              {/* preview contents */}
              <PreviewContents
                title='毎月の電子決済金額'
                uniqueId={`${uniqueProductTypeWord}-electronic-payment-${item.productId}`}
                paragraphValue={displayElectronicPayment}
              />
              {/* preview contents */}
              <PreviewContents
                title='クレジットカードご利用状況'
                uniqueId={`${uniqueProductTypeWord}-using-mobile-credit-card-${item.productId}`}
                paragraphValue={displayUsingMobileCreditCard()}
              />
            </>
          ) : (
            <>
              {commodityCategoryType === COMMODITY_CATEGORY_TYPE.SOFTBANK_HIKARI && (
                <>
                  {/* preview contents */}
                  <PreviewContents
                    title='契約中回線事業者'
                    uniqueId={`${uniqueProductTypeWord}-using-internet-line-provider-${item.productId}`}
                    // paragraphValue={displayUsingInternetLineProvider}
                    paragraphValue={displayUsingInternetLineProviderInfo}
                  />
                  {/* preview contents */}
                  <PreviewContents
                    title='契約中プロバイダ'
                    uniqueId={`${uniqueProductTypeWord}-using-landline-provider-${item.productId}`}
                    // paragraphValue={displayUsingLandLineProvider}
                    paragraphValue={displayUsingLandLineProviderInfo}
                  />
                </>
              )}
              {commodityCategoryType === COMMODITY_CATEGORY_TYPE.SOFTBANK_HIKARI_PLUS && (
                <>
                  {/* preview contents */}
                  <PreviewContents
                    title='契約中回線事業者'
                    uniqueId={`${uniqueProductTypeWord}-using-internet-line-provider-${item.productId}`}
                    paragraphValue={displayUsingInternetLineProviderInfoSBHikariPlus}
                  />
                  {/* preview contents */}
                  <PreviewContents
                    title='契約中プロバイダ'
                    uniqueId={`${uniqueProductTypeWord}-using-landline-provider-${item.productId}`}
                    paragraphValue={displayUsingLandLineProviderInfoSBHikariPlus}
                  />
                </>
              )}
              {commodityCategoryType === COMMODITY_CATEGORY_TYPE.ELECTRICITY && (
                <>
                  <PreviewContents
                    title='現在の電力事業者'
                    uniqueId={`${uniqueProductTypeWord}-using-pwr-provider-${item.productId}`}
                    paragraphValue={displayUsingPwrProvider}
                  />
                  {/* preview contents */}
                  <PreviewContents
                    title='電気料金'
                    uniqueId={`${uniqueProductTypeWord}-using-pwr-month-charge-${item.productId}`}
                    paragraphValue={displayUsingPwrMonthCharge()}
                  />
                </>
              )}
              {!arrayCarrierFree.includes(commodityCategoryType as COMMODITY_CATEGORY_TYPE) && (
                <PreviewContents
                  title='お住まい'
                  uniqueId={`${uniqueProductTypeWord}-housing-type-${item.productId}`}
                  paragraphValue={
                    commodityCategoryType === COMMODITY_CATEGORY_TYPE.ELECTRICITY ? displayUsingPwrHousingType : displayHousingType
                  }
                />
              )}
              {commodityCategoryType === COMMODITY_CATEGORY_TYPE.SOFTBANK_HIKARI && (
                <>
                  {/* preview contents */}
                  <PreviewContents
                    title='利用中のネット環境'
                    uniqueId={`${uniqueProductTypeWord}-using-internet-type-${item.productId}`}
                    paragraphValue={displayUsingHomeInternetType}
                  />
                </>
              )}
              {(commodityCategoryType === COMMODITY_CATEGORY_TYPE.SOFTBANK_HIKARI ||
                commodityCategoryType === COMMODITY_CATEGORY_TYPE.HOME_PHONE) && (
                <>
                  {/* preview contents */}
                  <PreviewContents
                    title='固定電話'
                    uniqueId={`${uniqueProductTypeWord}-fixed-line-status-${item.productId}`}
                    paragraphValue={
                      commodityCategoryType === COMMODITY_CATEGORY_TYPE.SOFTBANK_HIKARI
                        ? displayFixedLineStatusSbHikari
                        : displayFixedLineStatus
                    }
                  />
                </>
              )}
              {commodityCategoryType === COMMODITY_CATEGORY_TYPE.SOFTBANK_HIKARI && (
                <>
                  {/* preview contents */}
                  <PreviewContents
                    title='毎月のお支払い金額'
                    uniqueId={`${uniqueProductTypeWord}-using-sb-hikari-monthly-payment-${item.productId}`}
                    paragraphValue={displayUsingSbHikariMonthlyPayment}
                  />
                  {/* preview contents */}
                  <PreviewContents
                    title='現在回線の利用開始年月'
                    uniqueId={`${uniqueProductTypeWord}-using-start-year-month-${item.productId}`}
                    paragraphValue={getDisplayUsingStartYearMonth(DISPLAY_ITEM_ID.USING_SB_HIKARI_START_YEAR_MONTH)}
                  />
                  {/* preview contents */}
                  <PreviewContents
                    title='解約金'
                    uniqueId={`${uniqueProductTypeWord}-cancellation-fee-${item.productId}`}
                    paragraphValue={getDisplayCancellationFee(DISPLAY_ITEM_ID.SB_HIKARI_CANCELLATION_FEE)}
                  />
                </>
              )}
              {commodityCategoryType === COMMODITY_CATEGORY_TYPE.SOFTBANK_HIKARI_PLUS && (
                <>
                  {/* preview contents */}
                  <PreviewContents
                    title='利用中のネット環境'
                    uniqueId={`${uniqueProductTypeWord}-using-internet-type-${item.productId}`}
                    paragraphValue={getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.USING_HOME_INTERNET_TYPE_SB_HIKARI_PLUS)}
                  />
                  {/* preview contents */}
                  <PreviewContents
                    title='固定電話'
                    uniqueId={`${uniqueProductTypeWord}-fixed-line-status-${item.productId}`}
                    paragraphValue={getDisplayValueByVariableInfo(DISPLAY_ITEM_ID.FIXED_LINE_STATUS_SB_HIKARI)}
                  />
                  {/* preview contents */}
                  <PreviewContents
                    title='毎月のお支払い金額'
                    uniqueId={`${uniqueProductTypeWord}-using-sb-hikari-plus-monthly-payment-${item.productId}`}
                    paragraphValue={displayUsingSBHikariPlusMonthlyPayment}
                  />
                  {/* preview contents */}
                  <PreviewContents
                    title='現在回線の利用開始年月'
                    uniqueId={`${uniqueProductTypeWord}-using-start-year-month-${item.productId}`}
                    paragraphValue={getDisplayUsingStartYearMonth(DISPLAY_ITEM_ID.USING_SB_HIKARI_PLUS_START_YEAR_MONTH)}
                  />
                  {/* preview contents */}
                  <PreviewContents
                    title='解約金'
                    uniqueId={`${uniqueProductTypeWord}-cancellation-fee-${item.productId}`}
                    paragraphValue={getDisplayCancellationFee(DISPLAY_ITEM_ID.SB_HIKARI_PLUS_CANCELLATION_FEE)}
                  />
                </>
              )}
              {commodityCategoryType === COMMODITY_CATEGORY_TYPE.SOFTBANK_AIR && (
                <>
                  {/* preview contents */}
                  <PreviewContents
                    title='毎月のお支払い金額'
                    uniqueId={`${uniqueProductTypeWord}-using-sb-air-monthly-payment-${item.productId}`}
                    paragraphValue={displayUsingSbAirMonthlyPayment}
                  />
                  {/* preview contents */}
                  <PreviewContents
                    title='現在回線の利用開始年月'
                    uniqueId={`${uniqueProductTypeWord}-using-start-year-month-${item.productId}`}
                    paragraphValue={getDisplayUsingStartYearMonth(DISPLAY_ITEM_ID.USING_SB_AIR_START_YEAR_MONTH)}
                  />
                  {/* preview contents */}
                  <PreviewContents
                    title='解約金'
                    uniqueId={`${uniqueProductTypeWord}-cancellation-fee-${item.productId}`}
                    paragraphValue={getDisplayCancellationFee(DISPLAY_ITEM_ID.SB_AIR_CANCELLATION_FEE)}
                  />
                </>
              )}
              {commodityCategoryType === COMMODITY_CATEGORY_TYPE.HOME_PHONE && (
                <>
                  {/* preview contents */}
                  <PreviewContents
                    title='毎月のお支払い金額'
                    uniqueId={`${uniqueProductTypeWord}-using-landline-phone-monthly-payment-${item.productId}`}
                    paragraphValue={displayUsingLandlinePhoneMonthlyPayment}
                  />
                  {/* preview contents */}
                  <PreviewContents
                    title='現在回線の利用開始年月'
                    uniqueId={`${uniqueProductTypeWord}-using-start-year-month-${item.productId}`}
                    paragraphValue={getDisplayUsingStartYearMonth(DISPLAY_ITEM_ID.USING_LANDLINE_PHONE_START_YEAR_MONTH)}
                  />
                  {/* preview contents */}
                  <PreviewContents
                    title='解約金'
                    uniqueId={`${uniqueProductTypeWord}-cancellation-fee-${item.productId}`}
                    paragraphValue={getDisplayCancellationFee(DISPLAY_ITEM_ID.LANDLINE_PHONE_CANCELLATION_FEE)}
                  />
                </>
              )}
              {commodityCategoryType === COMMODITY_CATEGORY_TYPE.ELECTRICITY && (
                <>
                  {/* preview contents */}
                  <PreviewContents
                    title='ご希望プラン'
                    uniqueId={`${uniqueProductTypeWord}-pwr-hope-price-plan-${item.productId}`}
                    paragraphValue={displayPwrHopePricePlan}
                  />
                  {/* preview contents */}
                  <PreviewContents
                    title='現在電力事業者の利用開始年月'
                    uniqueId={`${uniqueProductTypeWord}-using-start-year-month-${item.productId}`}
                    paragraphValue={getDisplayUsingStartYearMonth(DISPLAY_ITEM_ID.USING_PWR_START_YEAR_MONTH)}
                  />
                  {/* preview contents */}
                  <PreviewContents
                    title='解約金'
                    uniqueId={`${uniqueProductTypeWord}-cancellation-fee-${item.productId}`}
                    paragraphValue={getDisplayCancellationFee(DISPLAY_ITEM_ID.PWR_CANCELLATION_FEE)}
                  />
                </>
              )}
              {commodityCategoryType === COMMODITY_CATEGORY_TYPE.DATA_BOX && (
                <>
                  {/* preview contents */}
                  <PreviewContents
                    title='自社／他社ユーザー（回線）'
                    uniqueId={`${uniqueProductTypeWord}-user-company-${item.productId}`}
                    paragraphValue={displayUsingUserCompanyDataBox}
                  />
                </>
              )}
              {commodityCategoryType === COMMODITY_CATEGORY_TYPE.BASKET_LIVE && (
                <>
                  {/* preview contents */}
                  <PreviewContents
                    title='自社／他社ユーザー（回線）'
                    uniqueId={`${uniqueProductTypeWord}-user-company-${item.productId}`}
                    paragraphValue={displayUsingUserCompanyBasketLive}
                  />
                </>
              )}
              {commodityCategoryType === COMMODITY_CATEGORY_TYPE.SMARTPHONE_SET_SUPPORT && (
                <>
                  {/* preview contents */}
                  <PreviewContents
                    title='自社／他社ユーザー（回線）'
                    uniqueId={`${uniqueProductTypeWord}-user-company-${item.productId}`}
                    paragraphValue={displayUsingUserCompanySmartphoneSetSupport}
                  />
                </>
              )}
            </>
          )}
        </Table.TBody>
      );
    };

    // 更新月
    const UpdYearMonthInfo = ({ commodityCategoryType }: ProductBaseProps) => {
      const uniqueWord =
        commodityCategoryType === COMMODITY_CATEGORY_TYPE.SOFTBANK_HIKARI ||
        commodityCategoryType === COMMODITY_CATEGORY_TYPE.SOFTBANK_AIR ||
        commodityCategoryType === COMMODITY_CATEGORY_TYPE.HOME_PHONE ||
        commodityCategoryType === COMMODITY_CATEGORY_TYPE.SOFTBANK_HIKARI_PLUS
          ? 'internet'
          : 'mobile';
      const displayWord =
        commodityCategoryType === COMMODITY_CATEGORY_TYPE.SOFTBANK_HIKARI_PLUS
          ? 'SoftBank 光＋更新月'
          : commodityCategoryType === COMMODITY_CATEGORY_TYPE.HOME_PHONE
          ? 'おうちのでんわ更新月'
          : commodityCategoryType === COMMODITY_CATEGORY_TYPE.SOFTBANK_HIKARI
          ? 'SB光更新月'
          : commodityCategoryType === COMMODITY_CATEGORY_TYPE.SOFTBANK_AIR
          ? 'SBAir更新月'
          : 'モバイル更新月';
      const headerUniqueId = `${uniqueProductTypeWord}-${uniqueWord}-upd-year-month-heading-${item.productId}`;
      const contentsUniqueId = `${uniqueProductTypeWord}-${uniqueWord}-upd-year-month-${item.productId}`;
      return (
        <Table.TBody>
          {/* header (small) */}
          <PreviewSmallCategoryHeader title='更新月' uniqueId={headerUniqueId} />
          {/* preview contents */}
          <PreviewContents
            title={displayWord}
            uniqueId={contentsUniqueId}
            paragraphValue={uniqueWord === 'internet' ? displayInternetUpdYearMonth : displayMobileUpdYearMonth}
          />
        </Table.TBody>
      );
    };

    // キャンペーン
    const CampaignInfo = () => (
      <Table.TBody>
        {/* header (small) */}
        <PreviewSmallCategoryHeader title='キャンペーン' uniqueId={`${uniqueProductTypeWord}-campaign-heading-${item.productId}`} />
        {/* preview contents */}
        <PreviewContents
          title='キャンペーン名'
          uniqueId={`${uniqueProductTypeWord}-campaign-name-${item.productId}`}
          paragraphValue={displayCampaignName}
        />
        {/* preview contents */}
        <PreviewContents
          title='キャンペーン適用開始月'
          uniqueId={`${uniqueProductTypeWord}-campaign-start-month-${item.productId}`}
          paragraphValue={displayCampaignMonth}
        />
      </Table.TBody>
    );

    // 割賦残債（モバイル）
    const InstallmentBalanceInfo = () => (
      <Table.TBody>
        {/* header (small) */}
        <PreviewSmallCategoryHeader title='割賦残債' uniqueId={`${uniqueProductTypeWord}-installment-balance-heading-${item.productId}`} />
        {usingMobileInstallEarlyLastCount() ? (
          <>
            {/* preview contents */}
            <PreviewContents
              title={displayMobileInstallEarlyLabel}
              uniqueId={`${uniqueProductTypeWord}-mobile-installment-balance-early-${item.productId}`}
              paragraphValue={displayMobileInstallmentBalanceEarly()}
            />
            {/* preview contents */}
            {usingMobileInstallMiddleLastCount() && (
              <PreviewContents
                title={displayMobileInstallMiddleLabel}
                uniqueId={`${uniqueProductTypeWord}-mobile-installment-balance-middle-${item.productId}`}
                paragraphValue={displayMobileInstallmentBalanceMiddle()}
              />
            )}
            {/* preview contents */}
            <PreviewContents
              title={displayMobileInstallLatestLabel}
              uniqueId={`${uniqueProductTypeWord}-mobile-installment-balance-latest-${item.productId}`}
              paragraphValue={displayMobileInstallmentBalanceLatest()}
            />
            {/* preview contents */}
            <PreviewContents
              title='残債総額'
              uniqueId={`${uniqueProductTypeWord}-mobile-installment-balance-total-${item.productId}`}
              paragraphValue={displayInstallmentBalanceTotal}
            />
          </>
        ) : (
          <>
            {/* preview contents */}
            <PreviewContents
              title='割賦単価'
              uniqueId={`${uniqueProductTypeWord}-mobile-install-monthly-payment-${item.productId}`}
              paragraphValue={displayMobileInstallMonthlyPaymentLatest}
            />
            {/* preview contents */}
            <PreviewContents
              title='支払終了月'
              uniqueId={`${uniqueProductTypeWord}-mobile-install-end-year-month-${item.productId}`}
              paragraphValue={displayMobileInstallEndYearMonthLatest}
            />
            {/* preview contents */}
            <PreviewContents
              title='残債総額'
              uniqueId={`${uniqueProductTypeWord}-mobile-installment-balance-total-${item.productId}`}
              paragraphValue={displayInstallmentBalanceSum}
            />
          </>
        )}
      </Table.TBody>
    );

    // 割賦残債（SBAir）
    const SBAirInstallmentBalanceInfo = () => (
      <Table.TBody>
        {/* header (small) */}
        <PreviewSmallCategoryHeader title='割賦残債' uniqueId={`${uniqueProductTypeWord}-installment-balance-heading-${item.productId}`} />
        {usingSBAirInstallEarlyLastCount() ? (
          <>
            {/* preview contents */}
            <PreviewContents
              title={displayUsingSBAirInstallEarlyLabel}
              uniqueId={`${uniqueProductTypeWord}-installment-balance-early-${item.productId}`}
              paragraphValue={displayUsingSBAirInstallmentBalanceEarly()}
            />
            {/* preview contents */}
            <PreviewContents
              title={displayUsingSBAirInstallLatestLabel}
              uniqueId={`${uniqueProductTypeWord}-installment-balance-latest-${item.productId}`}
              paragraphValue={displayUsingSBAirInstallmentBalanceLatest()}
            />
            {/* preview contents */}
            <PreviewContents
              title='残債総額'
              uniqueId={`${uniqueProductTypeWord}-installment-balance-total-${item.productId}`}
              paragraphValue={displayUsingSBAirInstallmentBalanceTotal}
            />
          </>
        ) : (
          <>
            {/* preview contents */}
            <PreviewContents
              title='割賦単価'
              uniqueId={`${uniqueProductTypeWord}-installment-monthly-payment-${item.productId}`}
              paragraphValue={displayUsingSBAirInstallMonthlyPaymentLatest}
            />
            {/* preview contents */}
            <PreviewContents
              title='支払終了月'
              uniqueId={`${uniqueProductTypeWord}-installment-end-year-month-${item.productId}`}
              paragraphValue={displayUsingSBAirInstallEndYearMonthLatest}
            />
            {/* preview contents */}
            <PreviewContents
              title='残債総額'
              uniqueId={`${uniqueProductTypeWord}-installment-balance-total-${item.productId}`}
              paragraphValue={displayUsingSBAirInstallmentBalanceSum}
            />
          </>
        )}
      </Table.TBody>
    );

    // 割賦残債（おうちのでんわ）
    const LandlinePhoneInstallmentBalanceInfo = () => (
      <Table.TBody>
        {/* header (small) */}
        <PreviewSmallCategoryHeader title='割賦残債' uniqueId={`${uniqueProductTypeWord}-installment-balance-heading-${item.productId}`} />
        {
          <>
            {/* preview contents */}
            <PreviewContents
              title='割賦単価'
              uniqueId={`${uniqueProductTypeWord}-installment-monthly-payment-${item.productId}`}
              paragraphValue={displayLandlinePhoneInstallMonthlyPaymentLatest}
            />
            {/* preview contents */}
            <PreviewContents
              title='支払終了月'
              uniqueId={`${uniqueProductTypeWord}-installment-end-year-month-${item.productId}`}
              paragraphValue={displayLandlinePhoneInstallEndYearMonthLatest}
            />
            {/* preview contents */}
            <PreviewContents
              title='残債総額'
              uniqueId={`${uniqueProductTypeWord}-installment-balance-total-${item.productId}`}
              paragraphValue={displayLandlinePhoneInstallmentBalanceSum}
            />
          </>
        }
      </Table.TBody>
    );

    // ご希望の内容
    const HopeContentsInfo = ({ showHopeKinds }: ProductHopeContentProps) => (
      <Table.TBody>
        {/* header (small) */}
        <PreviewSmallCategoryHeader title='ご希望の内容' uniqueId={`${uniqueProductTypeWord}-hope-contents-heading-${item.productId}`} />
        {showHopeKinds && (
          <>
            {/* preview contents */}
            <PreviewContents
              title='希望機種有無'
              uniqueId={`${uniqueProductTypeWord}-hope-kinds-flg-${item.productId}`}
              paragraphValue={displayHopKindsFlg}
            />
            {/* preview contents */}
            <PreviewContents
              title='希望機種'
              uniqueId={`${uniqueProductTypeWord}-hope-kinds-${item.productId}`}
              paragraphValue={hopeKindsInfo}
            />
          </>
        )}
        {!arrayCarrierFree.includes(item.commodityCategoryType as COMMODITY_CATEGORY_TYPE) && (
          <>
            <KeepTypeInfo />
            {/* preview contents */}
            <PreviewContents
              title='料金プラン'
              uniqueId={`${uniqueProductTypeWord}-price-plan-${item.productId}`}
              paragraphValue={displayPricePlan}
            />
            {/* preview contents */}
            <PreviewContents
              title='パケットサービス'
              uniqueId={`${uniqueProductTypeWord}-packet-service-${item.productId}`}
              paragraphValue={displayPacketDsctService}
            />
            {/* preview contents */}
            <PreviewContents
              title='店頭スマホサポート'
              uniqueId={`${uniqueProductTypeWord}-set-support-${item.productId}`}
              paragraphValue={displaySmartPhoneSetSupport()}
            />
            {/* preview contents */}
            <PreviewContents
              title='PayPay設定'
              uniqueId={`${uniqueProductTypeWord}-paypay-setting-${item.productId}`}
              paragraphValue={paypaySetting}
            />
          </>
        )}
        {item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.DATA_BOX && (
          <>
            {/* preview contents */}
            <PreviewContents
              title='お支払い方法'
              uniqueId={`${uniqueProductTypeWord}-payment-method-${item.productId}`}
              paragraphValue={displayPaymentMethodDataBox}
            />
          </>
        )}
        {item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.BASKET_LIVE && (
          <>
            {/* preview contents */}
            <PreviewContents
              title='お支払い方法'
              uniqueId={`${uniqueProductTypeWord}-payment-method-${item.productId}`}
              paragraphValue={displayPaymentMethodBasketLive}
            />
            {/* preview contents */}
            <PreviewContents
              title='料金プラン'
              uniqueId={`${uniqueProductTypeWord}-price-plan-${item.productId}`}
              paragraphValue={displayPricePlanBasketLive}
            />
          </>
        )}
        {item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.SMARTPHONE_SET_SUPPORT && (
          <>
            {/* preview contents */}
            <PreviewContents
              title='お支払い方法'
              uniqueId={`${uniqueProductTypeWord}-payment-method-${item.productId}`}
              paragraphValue={displayPaymentMethodSmartphoneSetSupport}
            />
            {/* preview contents */}
            <PreviewContents
              title='プラン'
              uniqueId={`${uniqueProductTypeWord}-plan-${item.productId}`}
              paragraphValue={displayPlanSmartphoneSetSupport()}
            />
          </>
        )}
      </Table.TBody>
    );

    // その他
    const OtherInfo = ({ commodityCategoryType }: ProductBaseProps) => (
      <Table.TBody>
        {/* header (small) */}
        <PreviewSmallCategoryHeader title='その他' uniqueId={`${uniqueProductTypeWord}-other-heading-${item.productId}`} />
        {/* preview contents */}
        <PreviewContents
          title='特記事項'
          uniqueId={`${uniqueProductTypeWord}-notices-${item.productId}`}
          paragraphValue={displayNotice({ commodityCategoryType })}
        />
      </Table.TBody>
    );

    // NTT回線名義
    const InternetContractInfo = () => (
      <Table.TBody>
        {/* header (small) */}
        <PreviewSmallCategoryHeader
          title='NTT回線名義'
          uniqueId={`${uniqueProductTypeWord}-internet-contract-holder-heading-${item.productId}`}
        />
        {/* preview contents */}
        <PreviewContents
          title='NTT116確認結果'
          uniqueId={`${uniqueProductTypeWord}-internet-contract-holder-${item.productId}`}
          paragraphValue={displayInternetContractHolder}
        />
      </Table.TBody>
    );

    return (
      <div ref={lastDivItemRef} id={`negotiation${negotiationId}product${item.productId}`}>
        {uniqueProductTypeWord && (
          <Table key={item.productId} className='preview-table u-mt32'>
            {/* header (middle) */}
            <PreviewMiddleCategoryHeader
              editInfo={
                isEditable
                  ? {
                      uniqueId: `${item.productId}`,
                      label: '編集',
                      onClickEditHandler: () =>
                        openPermissionModal && openPermissionModal('onEditProduct', `${negotiationId}`, `${item.productId}`),
                    }
                  : undefined
              }
              title={`商品${index + 1} - ${COMMODITY_CATEGORY_TAG[item.commodityCategoryType].name}`}
              uniqueId={`${uniqueProductTypeWord}-header-${item.productId}`}
            />
            {/* 基本情報 */}
            <BasicInfo commodityCategoryType={item.commodityCategoryType} />

            {/* エリアチェック - 光 */}
            {/* エリアチェック - SoftBank 光＋ */}
            {/* エリアチェック - Air */}
            {/* エリアチェック - でんわ */}
            {arrayInternet.includes(item.commodityCategoryType as COMMODITY_CATEGORY_TYPE) && <AreaCheckInfo />}

            {/* ご利用状況 (その他以外は全て表示)*/}
            {item.commodityCategoryType !== COMMODITY_CATEGORY_TYPE.ALL_OTHER && UsageStatusInfo(item.commodityCategoryType)}

            {/* 割賦残債 - おうちのでんわ */}
            {item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.HOME_PHONE && <LandlinePhoneInstallmentBalanceInfo />}

            {/* NTT回線名義 - SB光 , おうちのでんわ */}
            {(item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.SOFTBANK_HIKARI ||
              item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.HOME_PHONE) && <InternetContractInfo />}

            {/* 更新月 */}
            {/* おうちのでんき,その他以外 */}
            {item.commodityCategoryType !== COMMODITY_CATEGORY_TYPE.ELECTRICITY &&
              item.commodityCategoryType !== COMMODITY_CATEGORY_TYPE.ALL_OTHER &&
              !arrayCarrierFree.includes(item.commodityCategoryType as COMMODITY_CATEGORY_TYPE) && (
                <UpdYearMonthInfo commodityCategoryType={item.commodityCategoryType} />
              )}

            {/* あんしんデータボックス, バスケットLIVE, 店頭スマホサポート（他社） */}
            {arrayCarrierFree.includes(item.commodityCategoryType as COMMODITY_CATEGORY_TYPE) && (
              <>
                {/* ご希望の内容 */}
                <HopeContentsInfo showHopeKinds={false} />
              </>
            )}

            {/* モバイルの場合 */}
            {arrayMobile.includes(item.commodityCategoryType as COMMODITY_CATEGORY_TYPE) && (
              <>
                {/* キャンペーン */}
                <CampaignInfo />
                {/* 割賦残債 */}
                <InstallmentBalanceInfo />
                {/* ご希望の内容 */}
                <HopeContentsInfo showHopeKinds={item.commodityCategoryType !== COMMODITY_CATEGORY_TYPE.SIM_ONLY} />
                {/* その他 */}
                <OtherInfo commodityCategoryType={item.commodityCategoryType} />
              </>
            )}
            {/* 割賦残債 - SBAir */}
            {item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.SOFTBANK_AIR && <SBAirInstallmentBalanceInfo />}

            {/* 取り置き - おうちのでんわ */}
            {(item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.SOFTBANK_AIR ||
              item.commodityCategoryType === COMMODITY_CATEGORY_TYPE.HOME_PHONE) && (
              <Table.TBody>
                {/* header (small) */}
                <PreviewSmallCategoryHeader title='取り置き' uniqueId={`${uniqueProductTypeWord}-keep-type-heading-${item.productId}`} />
                {/* preview contents */}
                <KeepTypeInfo />
              </Table.TBody>
            )}
          </Table>
        )}
      </div>
    );
  },
);

export default ProductInfoPreview;
